import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Icon } from 'src/assets/images/fortune/ic-nookpoint.svg';
import { ConfirmButton } from 'src/components/shared/buttons/ConfirmButton';
import { FadeInImage } from 'src/components/shared/FadeInImage';
import { formatNumber } from 'src/lib/format-number';
import { catalogIconUrl } from 'src/models/catalog/CatalogItem';
import { FortuneItem } from 'src/models/fortune/FortuneItem';

interface ContentProps {
  item: FortuneItem;
  ownedPoint: number;
}

const waveDecoration =
  'data:image/svg+xml,%3Csvg width="375" height="12" viewBox="0 0 375 12" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M187.5 0c-47.833 0-71.077 7.91-133.594 7.91C30.413 7.91 0 4.271 0 4.271v7.714h375V4.272S344.587 7.91 321.094 7.91C258.577 7.91 235.333 0 187.5 0z" fill="%23E0F4BA" fill-opacity=".5" /%3E%3Cpath d="M187.5 5.993c-47.833 0-71.077 3.83-133.594 3.83C30.413 9.824 0 7.988 0 7.988V12h53.906c69.618 0 81.484-.014 133.594-.014h133.594C340.588 11.986 375 12 375 12V7.987s-30.413 1.837-53.906 1.837c-62.517 0-85.761-3.83-133.594-3.83z" fill="%23E0F4BA" /%3E%3C/svg%3E%0A';

export const FortuneItemDetailContent: React.FC<ContentProps> = ({ item, ownedPoint }) => {
  const { t, i18n } = useTranslation(['fortune', 'translation']);
  const history = useHistory();
  const haveEnoughPoint = item.point <= ownedPoint;

  const handleClick = useCallback(() => {
    history.push(`/players/fortune/exchange/detail/${item.unique_id}/confirm`);
  }, [history, item.unique_id]);
  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        height: 100%;
        width: 100%;

        .body {
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: auto;
        }
        .image-area {
          position: relative;
          background: #f4ffe0;
          background-image: url('/images/fortune/img_nookpoint_bg_01.png');
        }
        .container-image {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          max-width: 400px;
          margin: auto;
          padding: 20px;
  
          &::before {
            content: '';
            display: block;
            padding-top: 65%;
          }
  
          @media (min-width: 480px) {
            &::before {
              padding-top: 90%;
            }
          }
        }
  
        .wave {
          position: absolute;
          bottom: -1px;
          left: 0;
          right: 0;
          height: 16px;
          background-image: url('${waveDecoration}');
          background-repeat: no-repeat;
          background-size: cover;
          background-position: center;
        }
        .info-area {
          flex: 1 1 auto;
          background-color: #e0f4ba;
        }
        .detail-content {
          max-width: 480px;
          padding: 28px 20px 20px;
          margin: auto;
        }
        .label {
          color: #4D3906;
          font-size: 24px;
        }
        .required-point {
          display: flex;
          align-items: center;
          justify-content: space-between;
          color: #33a142;
          font-size: 14px;
          padding-bottom:8px;
          margin-top: 24px;
        }
        .required-point-value {
          display: flex;
          align-items: center;
          font-size: 32px;
        }
        .footer {
          flex: 0 0 auto;
          width: 100%;
          background-color: #e0f4ba;
        }
        .button {
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
          padding: 12px 16px 24px;
        }
        @media (max-width: 374px) {
          .label {
            font-size: 20px;
          }
        }
      `}
    >
      <div className="body">
        <div className="image-area">
          <div className="container-image">
            <FadeInImage
              src={catalogIconUrl(item.icon)}
              width={128}
              height={128}
              style={{ width: '128px', height: '128px' }}
              ariaLabel={t('translation:aria_item_image')}
            />
          </div>
          <div className="wave"></div>
        </div>
        <div className="info-area">
          <div className="detail-content">
            <h3 className="label">{item.label}</h3>
            <div
              className="required-point"
              aria-labelledby={'required-point-label required-point-value'}
            >
              <span id="required-point-label">{t('required_points')}</span>
              <div className="required-point-value">
                <Icon width={36} height={36} style={{ margin: '0 2px 0 0' }} />
                <span id="required-point-value">{formatNumber(item.point, i18n.language)}</span>
              </div>
            </div>

            {/* debug log */}
            {process.env.REACT_APP_DEBUG_OPTION === 'true' && (
              <pre style={{ fontSize: '12px', margin: '80px 0 20px', overflow: 'scroll' }}>
                {(() => {
                  return JSON.stringify(item, null, 2);
                })()}
              </pre>
            )}
          </div>
        </div>
      </div>
      <div className="footer">
        <div className="button">
          <ConfirmButton onClick={handleClick} disabled={!haveEnoughPoint}>
            {haveEnoughPoint ? t('button_exchange') : t('button_lack_of_points')}
          </ConfirmButton>
        </div>
      </div>
    </div>
  );
};
